define((require) => {
    "use strict";

    const Backbone     = require("backbone");
    const CompanyState = require("shared/data/CompanyState");
    const {Logger}       = require("shared/util/Logger");

    const log = new Logger("Login"); // name same as in Login module!

    /**
     * A standalone wrapper for the potentially logged in principal, needed to avoid nasty circular dependencies with
     * the <tt>Login</tt> service. <p>
     *
     * To get the currently logged in principal, do <tt>LoggedIn.principal()</tt>; will be null if no principal
     * is logged in. In addition, the convenience function <tt>ad():Boolean</tt> will return true if a principal
     * is logged in via the AD, false otherwise. <p>
     *
     * To listen for changes to login, do <tt>LoggedIn.on("change:login", function(principal) { ..not null if logged in.. });</tt>.
     *
     * To listen for changes to the user backing the currently logged in principal, for example on name change, do
     * <tt>LoggedIn.on("change:user", function(principal) { ..principal never null, principal.get("user") changed.. });</tt>.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     */
    const LoggedIn = Backbone.Model.extend({

        /**
         * @private
         * @type {Principal}
         * The currently logged in principal if not null.
         */
        _principal: null,

        /**
         * @private
         * Triggers a principal change: logged in, re-login, or logout (null principal).
         */
        _trigger() {
            this.trigger("change:login", this._principal);
        },

        /**
         * @private
         * Handles an external principal change - that should never happen!
         */
        _handleChange() { // this will never catch silent changes... :(
            log.error("Principal changed - logging out!");
            this._logout();
        },

        /**
         * @private
         * Handle user confirmation by potentially upgrading the role to the actual one now confirmed. The backend
         * does all the hard work, this is just to ensure frontend consistency without having to do a full reload...
         */
        _handleConfirmed() {
            const user = this.user();

            if (user) {
                user.off("change:confirmedAt", this._handleConfirmed);
                // We know the token will never be read only, since just confirmed, but we may still have company
                // restrictions. We never want to change principal roles on AD login...
                if (this._principal.get("type") !== "AD" && this._principal.get("state") === CompanyState.ACTIVE) {
                    const role = user.get("role");
                    this._principal.set({
                        type:    "Normal",
                        role,
                        updated: role !== this._principal.get("role")
                    }, {silent: true});
                }

                log.info(`User confirmed: ${this._principal}`);

                // Re-login to trigger change...
                this._trigger();
            }
        },

        /**
         * @private
         * When a new user logs in and sets the name (and password), we need to make sure the principal display name
         * matches <b>or</b> when the user edits her own name via the settings page.
         */
        _handleName() {
            const user = this.user();

            if (user) {
                // Never update principal display name on AD since it cannot change; the principal can edit the name
                // of the logged in user, but not of the principal...
                if (this._principal.get("type") !== "AD") {
                    this._principal.set("displayName", user.get("name"), {silent: true});
                }
                // This is unfortunately needed to to detect changes to the backing user higher up...
                this.trigger("change:user", this._principal);
            }
        },

        /**
         * @private
         * Logs in the supplied principal, if not null.
         *
         * @param {Principal} principal The principal that has logged in; never null.
         */
        _login(principal) { // actually "public" - called from Login, to avoid circular dependencies!
            if (principal) {
                this._principal = principal;
                // Make sure the principal is not changed...
                this._principal.on("change", this._handleChange);

                // Listen for user confirmation if not confirmed...
                const user = this._principal.get("user");

                if (!user.isConfirmed()) {
                    user.on("change:confirmedAt", this._handleConfirmed);
                }
                // Listen for relogin *or* edit name...
                user.on("change:name", this._handleName);

                log.info(`Logged in: ${this._principal}`);
                this._trigger();
            }
        },

        /**
         * @private
         * Logs out the current supplied principal, if not null.
         *
         * @param {Principal} [principal] The principal, if any, that just failed to log in, presumably different
         *                                from the principal now logged out. This is used to trigger an
         *                                "error:login_restricted" event.
         */
        _logout(principal) { // actually "public" - called from Login, to avoid circular dependencies!
            if (this._principal) {
                log.info(`Log out: ${this._principal}`);

                try {
                    this._principal.off();
                    this._principal.get("user").off("change:confirmedAt", this._handleConfirmed);
                } finally {
                    this._principal = null;
                    this._trigger();
                }
            }

            if (principal) {
                this.trigger("error:login_restricted", principal);
            }
        },

        /**
         * @private
         * Triggers a <tt>login:tooManyAttempts</tt> event with the lookout time in seconds as the second event
         * argument and the currently logged in principal as the first, if any. <p>
         *
         * Note that a principal can be signed in, i.e. this can occur both on first sign in, but also if the principal
         * tries to re sign in when trying to upgrade a read only token.
         *
         * @param {Number} lookout The lookout time in seconds; a falsey value will default to 5 minutes worth of
         *                         timeout.
         */
        _tooManyAttempts(lookout) {
            this.trigger("login:tooManyAttempts", this._principal, lookout || 5 * 60); // principal can be falsey!
        },

        /**
         * Returns the currently logged in principal, if any. <p>
         *
         * The test for roles and privileges are <b>always</b> performed via this principal, not via the
         * associated user. The user associated with the principal is available as <tt>principal().get("user")</tt>.
         *
         * @return {Principal} The currently logged in principal; can be null.
         * @see    #user()
         */
        principal() {
            return this._principal;
        },

        /**
         * Returns the user associated with the currently logged in principal, if any. This is just syntactic
         * sugar for <tt>var user = principal() && principal().get("user")</tt>. <p>
         *
         * The returned user is <b>not</b> used to test for roles and privileges, <tt>principal()</tt> is!
         *
         * @return {User} The user associated with currently logged in principal; can be null. If no principal,
         *                null will be returned.
         * @see    #principal()
         */
        user() {
            return this._principal && this._principal.get("user");
        },

        /**
         * Returns the id of the company for the user associated with the currently logged in principal, if any.
         * This is just syntactic sugar for <tt>var companyId = principal() && principal().get("companyId")</tt>.
         *
         * @return {String} The id of the company associated with currently logged in principal; can be null. If no
         *                  principal, null will be returned.
         * @see    #user()
         */
        companyId() {
            return this._principal && this._principal.get("companyId");
        },

        /**
         * Returns true if and only if a principal is currently logged in via the AD, false otherwise.
         *
         * @return {Boolean} True if and only if a principal is currently logged in via the AD, false otherwise.
         */
        ad() {
            return this._principal && this._principal.get("type") === "AD";
        },

        /**
         * Returns true if and only if a principal is currently logged in on the internal network, false otherwise. <p>
         *
         * The internal flag <b>must</b> not be used to test for privileged information, but fine to use to log/show
         * information depending on it.
         *
         * @return {Boolean} True if and only if a principal is currently logged on the internal network, false otherwise.
         */
        internal() {
            return this._principal && this._principal.get("internal");
        },

        initialize() {
            this._handleChange    = this._handleChange.bind(this);
            this._handleConfirmed = this._handleConfirmed.bind(this);
            this._handleName      = this._handleName.bind(this);
        }

    });

    // Listenable singleton...
    return new LoggedIn();
});



// WEBPACK FOOTER //
// ./src/shared/security/LoggedIn.js